import { useEffect } from 'react';

import { Container } from '@grupoboticario/flora-react';
import { useNavigate } from 'react-router-dom';

import { ErrorBoundaries, PageInfo, ResponsiveFloraTypography, Skeleton } from 'components';
import { useResellerDetail, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';

import { CampaignContentSkeleton, ResaleIntention, CampaignContent } from './../../components';
import { useLoyaltyIncentiveCampaign } from './../../hooks';
import Styles from './IncentiveCampaign.styles';
import { INCENTIVE_CAMPAIGN } from '../../IncentiveCampaign.d';

import type { AxiosResponse } from 'axios';

export const IncentiveCampaign = () => {
  const navigate = useNavigate();
  const {
    data: incentiveCampaign,
    isResaleIntention,
    isLoading,
    isError,
    isSuccess,
    isResultActive,
    error: incentiveCampaignError,
    shouldShowCampaign,
    isNotFound,
  } = useLoyaltyIncentiveCampaign();
  const { sendDataToGTM } = useTracking();
  const {
    data: resellerDetail,
    isLoading: isResellerLoading,
    isSuccess: isResellerSuccess,
    isError: isResellerError,
  } = useResellerDetail();
  const isLoyaltyMember = resellerDetail?.reseller.isLoyaltyMember ?? false;

  useEffect(() => {
    if (!isLoading) {
      const error = incentiveCampaignError as AxiosResponse;
      newRelicMonitoringService.addPageAction('callback', {
        event_name: 'conteudo_campanha_de_incentivos',
        page_title: 'Campanha de Incentivos',
        cd_detail: isSuccess ? 'sucesso' : `erro: ${error.status}`,
        user_not_found: isNotFound,
        result_active: isResultActive,
        loyalty_member: isLoyaltyMember,
      });
      if (incentiveCampaign !== undefined && isResellerSuccess) {
        sendDataToGTM({
          ...(isSuccess && {
            cd_rep_mcm: isLoyaltyMember,
            userid: resellerDetail?.reseller.resellerId,
            cd_ciclo: resellerDetail?.reseller.currentCycle,
          }),
          ...(isError && {
            cd_error_message: 'tivemos-um-problema-para-carregar-a-página',
          }),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isSuccess,
    isError,
    isResellerSuccess,
    incentiveCampaignError,
    resellerDetail,
    isNotFound,
    isResultActive,
  ]);

  useEffect(() => {
    if (!shouldShowCampaign && !isLoading) {
      return navigate('/');
    }
  }, [shouldShowCampaign, isLoading, navigate]);

  useEffect(() => {
    if (!isResellerLoading) {
      sendDataToGTM({
        ...(isResellerError && {
          cd_error_message: 'tivemos-um-problema-para-carregar-as-informações-do-revendedor',
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResellerLoading, isResellerSuccess, isResellerError]);

  if (isError) {
    return (
      <ErrorBoundaries.ReloadPage
        title='Tivemos um problema para carregar a página.'
        data-autotagging-flora-section={document}
      />
    );
  }

  return (
    <Styles.Wrapper>
      <Styles.PageInfoWrapper>
        <PageInfo
          breadcrumbs={[
            {
              text: 'Campanha de Incentivos',
              url: '',
            },
          ]}
        >
          {isLoading ? (
            <Skeleton data-testid='skeleton-heading' height='$18' width='50%' />
          ) : (
            <ResponsiveFloraTypography fontSize='title' fontWeight='bold' as='h1'>
              {incentiveCampaign?.pageTitle}
            </ResponsiveFloraTypography>
          )}
        </PageInfo>
      </Styles.PageInfoWrapper>
      <Container size='large' data-autotagging-flora-section={INCENTIVE_CAMPAIGN.FLORA_SECTION}>
        {isLoading ? (
          <CampaignContentSkeleton />
        ) : (
          <>{isResaleIntention ? <ResaleIntention /> : <CampaignContent />}</>
        )}
      </Container>
    </Styles.Wrapper>
  );
};
